import React from "react";
import { Trash2 } from "lucide-react";
import { cn } from "../../../lib/utils";

import { CREDIT_FACILITY_OPTIONS } from "../../../api/creditAccounts";

import type { OptionNameValuePair } from "../../../lib/interfaces/types";
import type { FieldErrors } from "../../create-case/utils/creditValidation";

import ButtonV2 from "../../../components/ui/button/v2";
import FormField, { useTouched } from "../../create-case/components/party-involved/FormField";
import AccountNameSearchInput, { StakeholderOption } from "./AccountNameSearchInput";
import { CreateCreditAccountData, CreditAccountProduct, CreditFacility } from "../../../api/credit_accounts";

export type CreateCreditAccountDataWithId = Partial<CreateCreditAccountData> & {
  account_number: string;
};

interface AccountRowProps {
  account: CreateCreditAccountDataWithId;
  errors: FieldErrors;
  productOptions: OptionNameValuePair<CreditAccountProduct>[];
  stakeholderOptions: StakeholderOption[];
  showAllFields: boolean;
  showLabels: boolean;
  showErrors: boolean;
  disabled: boolean;
  canDelete: boolean;
  onCreditAccountChange: (patch: Partial<CreateCreditAccountData>) => void;
  onAddStakeholder: (name: string) => void;
  onDelete: () => void;
}

const AccountRow: React.FC<AccountRowProps> = ({
  account: row,
  errors,
  productOptions,
  stakeholderOptions,
  showAllFields,
  showLabels,
  showErrors,
  disabled,
  canDelete,
  onCreditAccountChange,
  onAddStakeholder,
  onDelete,
}) => {
  const { isTouched, markTouched } = useTouched();
  const err = (f: string) => (showErrors || isTouched(f) ? errors[f] : undefined);

  return (
    <div
      className={cn(
        "grid gap-3 items-start p-3 -mx-3 rounded-md transition-colors duration-150 hover:bg-[#F3F4F6]",
        showAllFields
          ? "grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto]"
          : "grid-cols-1 md:grid-cols-[1fr_1fr_auto]"
      )}
    >
      <FormField
        type="text"
        label="Loan Account Number"
        required
        placeholder="Enter account number"
        value={row.ext_account_number || ""}
        error={err("extAccountNumber")}
        disabled={disabled}
        hideLabel={!showLabels}
        onChange={(v) => onCreditAccountChange({ ext_account_number: v })}
        onBlur={() => markTouched("extAccountNumber")}
      />
      <FormField
        type="select"
        label="Product"
        required
        placeholder="Select product"
        value={row.product || ""}
        options={productOptions}
        error={err("product")}
        disabled={disabled}
        hideLabel={!showLabels}
        onChange={(v) => onCreditAccountChange({ product: v as CreditAccountProduct })}
        onBlur={() => markTouched("product")}
      />

      {showAllFields && (
        <>
          <FormField
            type="select"
            label="Credit Facility"
            required
            placeholder="Select type"
            value={row.credit_facility || ""}
            options={CREDIT_FACILITY_OPTIONS}
            error={err("creditFacility")}
            disabled={disabled}
            hideLabel={!showLabels}
            onChange={(v) => onCreditAccountChange({ credit_facility: v as CreditFacility})}
            onBlur={() => markTouched("creditFacility")}
          />
          <AccountNameSearchInput
            accountName={row.account_name || ""}
            selectedStakeholderId={row.primary_borrower_stakeholder_id}
            options={stakeholderOptions}
            onSelectStakeholder={(stakeholderId) => {onCreditAccountChange({ primary_borrower_stakeholder_id: stakeholderId})}}
            onAddStakeholder={onAddStakeholder}
            product={row.product}
            required
            disabled={disabled}
            hideLabel={!showLabels}
            error={err("accountName")}
            onBlur={() => markTouched("accountName")}
          />
        </>
      )}

      <div className={cn("flex items-center", showLabels && "pt-5")}>
        <ButtonV2
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={disabled || !canDelete}
          title="Delete loan account"
          className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
          prefix={<Trash2 width="16" height="16" />}
        />
      </div>
    </div>
  );
};

export default AccountRow;
